import React from 'react';
import {styled} from 'styled-components';
import {colors} from '../../styles/colors';
import DefaultImageView from './DefaultImageView';
import {useFormatMsg} from 'utils/useFormatMsg';
import {minmax} from 'utils/minmax';

/**
 * 기본 입력 컴포넌트의 Props 인터페이스
 * @property {'password' | 'text' | 'date' | 'number'} [type] - 입력 타입
 * @property {any} value - 입력값
 * @property {React.CSSProperties} [placeholderStyle] - 플레이스홀더 스타일
 * @property {(text: string) => void} [setValue] - 값 변경 핸들러
 * @property {React.CSSProperties} [wrapperStyle] - 래퍼 스타일
 * @property {React.CSSProperties} [inputStyle] - 입력 필드 스타일
 * @property {boolean} [isVisiblePassword] - 비밀번호 표시 여부
 * @property {(value: boolean) => void} [setVisiblePassword] - 비밀번호 표시 상태 변경 핸들러
 * @property {(event: any) => void} [onKeyPress] - 키 입력 이벤트 핸들러
 * @property {React.MutableRefObject<any>} [reference] - 참조 객체
 * @property {string} [id] - 요소 ID
 * @property {boolean} [disabled] - 비활성화 여부
 * @property {string} [min] - 최소값
 * @property {string} [max] - 최대값
 * @property {string} [placeholderText] - 플레이스홀더 텍스트
 * @property {boolean} [readOnly] - 읽기 전용 여부
 * @property {() => void} [onClick] - 클릭 이벤트 핸들러
 * @property {() => void} [onFocus] - 포커스 이벤트 핸들러
 */
interface IDefaultInput {
  type?: 'password' | 'text' | 'date' | 'number';
  value: any;
  placeholderStyle?: React.CSSProperties;
  setValue?: (text: string) => void;
  wrapperStyle?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  isVisiblePassword?: boolean;
  setVisiblePassword?: (value: boolean) => void;
  onKeyPress?: (event: any) => void;
  reference?: React.MutableRefObject<any>;
  id?: string;
  disabled?: boolean;
  min?: string;
  max?: string;
  placeholderText?: string;
  readOnly?: boolean;
  onClick?: () => void;
  onFocus?: () => void;
}

/**
 * 기본 입력 컴포넌트
 * 공통으로 사용되는 입력 필드 컴포넌트입니다.
 * @param {IDefaultInput} props - 입력 컴포넌트의 props
 * @returns {JSX.Element} 기본 입력 컴포넌트
 */
const DefaultInput = ({
  type = 'text',
  value,
  setValue,
  wrapperStyle,
  placeholderStyle,
  isVisiblePassword,
  setVisiblePassword,
  inputStyle,
  onKeyPress,
  reference,
  id,
  disabled,
  min,
  max,
  placeholderText,
  readOnly,
  onClick,
}: IDefaultInput) => {
  const placeholder = placeholderText
    ? useFormatMsg(placeholderText) || placeholderText
    : '';

  return (
    <div style={{position: 'relative', ...wrapperStyle}}>
      <InputStyle
        id={id}
        ref={reference}
        placeholder={placeholder}
        value={value || ''}
        onChange={(e: any) => setValue && setValue(e.target.value)}
        style={inputStyle}
        type={isVisiblePassword ? 'text' : type}
        $placeholderStyle={placeholderStyle}
        onKeyPress={onKeyPress ? (e: any) => onKeyPress(e) : undefined}
        disabled={disabled}
        min={min}
        max={max}
        readOnly={readOnly}
        onClick={onClick}
        onWheel={(e: any) => e.currentTarget.blur()}
      />
      {type === 'password' && setVisiblePassword && (
        <EyeContainer onClick={() => setVisiblePassword(!isVisiblePassword)}>
          <DefaultImageView
            source={
              isVisiblePassword
                ? 'images/common/eye_open.png'
                : 'images/common/eye_close.png'
            }
            style={{height: '100%', cursor: 'pointer'}}
          />
        </EyeContainer>
      )}
    </div>
  );
};

const InputStyle = styled.input<{$placeholderStyle?: React.CSSProperties}>`
  font-family: 'Noto Sans KR', sans-serif;
  font-size: ${minmax(20)};
  font-weight: 400;
  color: ${colors.black};
  border-radius: ${minmax(10)};
  border: 1px solid ${colors.borderGray};
  background-color: ${colors.white};
  width: 100%;
  height: 100%;
  padding: 0 ${minmax(20)};
  outline: none;
  &:focus {
    border: solid 1px ${colors.lightGrey};
  }
  /* caret-color: ${colors.primary}; */
  &::placeholder {
    color: ${colors.lightGrey};
    font-size: ${minmax(20)};
    ${(props: any) => props.$placeholderStyle}
  }
`;

const EyeContainer = styled.div`
  position: absolute;
  width: 5%;
  height: 100%;
  display: flex;
  right: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

export default DefaultInput;
